/**
 * Shared professional-situation fields for tutor and parent forms.
 */

import * as React from 'react';
import { Select } from '@/components/forms/Select';
import { Input } from '@/components/forms/Input';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { DepartmentSelect } from './DepartmentSelect';
import { useEstablishmentsAutocomplete } from '@/hooks/useEstablishmentsAutocomplete';
import { useEnterprisesAutocomplete } from '@/hooks/useEnterprisesAutocomplete';
import { createEnterprise } from '@/services/enterprises.service';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/forms/Button';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { isRemoteValuedEngagementRequired } from '@/utils/professional-situation-utils';
import { Briefcase } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';
import {
  ACTIVITY_SECTOR_OPTIONS,
  CIVIL_SERVICE_BRANCH_OPTIONS,
  FIELD_OF_ACTIVITY_TUTOR_OPTIONS,
  ASSOCIATIVE_ENGAGEMENT_TOOLTIP,
  HIGH_SCHOOL_CURRICULUM_OPTIONS,
  PRO_SITUATION_STATUS_OPTIONS,
  STUDY_LEVEL_OPTIONS,
  TUTORING_COURSE_OPTIONS,
  YES_NO_OPTIONS,
} from '@/constants/shared-catalog';

/** Props for {@link ProfessionalSituationFields}. */
interface ProfessionalSituationFieldsProps {
  formData: Record<string, any>;
  onChange: (key: string, value: any) => void;
  persona: 'tutor' | 'parent';
}

/** Form fields for a user's professional situation. */
export const ProfessionalSituationFields: React.FC<ProfessionalSituationFieldsProps> = ({
  formData,
  onChange,
  persona,
}) => {
  const {
    options: highSchoolEstablishmentOptions,
    isLoading: highSchoolEstablishmentLoading,
    onSearch: handleHighSchoolEstablishmentSearch,
  } = useEstablishmentsAutocomplete({ types: ['lycee', 'professionnel'] });

  const {
    options: higherEdEstablishmentOptions,
    isLoading: higherEdEstablishmentLoading,
    onSearch: handleHigherEdEstablishmentSearch,
  } = useEstablishmentsAutocomplete();

  const {
    options: enterpriseOptions,
    isLoading: enterpriseLoading,
    onSearch: handleEnterpriseSearch,
  } = useEnterprisesAutocomplete();

  const [isCreatingEnterprise, setIsCreatingEnterprise] = React.useState(false);
  const [newEnterpriseName, setNewEnterpriseName] = React.useState('');

  const createEnterpriseMutation = useMutation({
    mutationFn: (name: string) => createEnterprise(name),
  });

  const { companyName } = useTheme();
  const partnerLabel = companyName
    ? `Son entreprise est-elle partenaire de ${companyName} ?`
    : 'Son entreprise est-elle partenaire ?';

  const proSituation = formData.professionalSituation || {};

  /** Edit-mode seed so Autocomplete can show the establishment name before a search. */
  const seedOption = React.useMemo(() => {
    const id = proSituation.establishment;
    const name = proSituation.establishmentName;
    if (!id || !name) return null;
    return { value: id as string, label: name as string };
  }, [proSituation.establishment, proSituation.establishmentName]);

  /** High-school options plus the seeded establishment when it is not already in the list. */
  const enrichedHighSchoolOptions = React.useMemo(() => {
    if (!seedOption) return highSchoolEstablishmentOptions;
    if (highSchoolEstablishmentOptions.some((o) => o.value === seedOption.value))
      return highSchoolEstablishmentOptions;
    return [seedOption, ...highSchoolEstablishmentOptions];
  }, [highSchoolEstablishmentOptions, seedOption]);

  /** Higher-ed options plus the seeded establishment when it is not already in the list. */
  const enrichedHigherEdOptions = React.useMemo(() => {
    if (!seedOption) return higherEdEstablishmentOptions;
    if (higherEdEstablishmentOptions.some((o) => o.value === seedOption.value))
      return higherEdEstablishmentOptions;
    return [seedOption, ...higherEdEstablishmentOptions];
  }, [higherEdEstablishmentOptions, seedOption]);

  /** Edit-mode enterprise seed (falls back to companyName; `__manual__` when there is no id). */
  const enterpriseSeedOption = React.useMemo(() => {
    const id = proSituation.employerId;
    const name = proSituation.employerName || proSituation.companyName;
    if (!name) return null;
    return { value: id || '__manual__', label: name };
  }, [proSituation.employerId, proSituation.employerName, proSituation.companyName]);

  /** Enterprise options plus the seeded enterprise when it is not already in the list. */
  const enrichedEnterpriseOptions = React.useMemo(() => {
    if (!enterpriseSeedOption) return enterpriseOptions;
    if (enterpriseOptions.some((o) => o.value === enterpriseSeedOption.value))
      return enterpriseOptions;
    return [enterpriseSeedOption, ...enterpriseOptions];
  }, [enterpriseOptions, enterpriseSeedOption]);

  const currentStatus = proSituation.status || '';

  /** Patch one professional-situation field on the parent form. */
  const handleChange = (field: string, value: any) => {
    const updated = { ...proSituation, [field]: value };
    onChange('professionalSituation', updated);
  };

  /** Persist both the establishment id and display name so the field can be re-seeded. */
  const handleEstablishmentChange = (
    value: string,
    options: { value: string; label: string }[],
  ) => {
    const selected = options.find((o) => o.value === value);
    onChange('professionalSituation', {
      ...proSituation,
      establishment: value,
      establishmentName: value ? (selected?.label ?? proSituation.establishmentName ?? '') : '',
    });
  };

  /** Tutors reset all sub-fields on situation change; parents keep existing fields. */
  const handleStatusChange = (newStatus: string) => {
    if (persona === 'tutor') {
      onChange('professionalSituation', { status: newStatus });
    } else {
      onChange('professionalSituation', { ...proSituation, status: newStatus });
    }
  };

  /** Persist enterprise id plus both name fields used by the nested payload. */
  const handleEnterpriseChange = (value: string, options: { value: string; label: string }[]) => {
    const selected = options.find((o) => o.value === value);
    onChange('professionalSituation', {
      ...proSituation,
      employerId: value,
      employerName: selected?.label ?? '',
      companyName: selected?.label ?? '',
    });
  };

  /** Create an enterprise via the API and write its id/name onto the form. */
  const handleCreateEnterprise = async () => {
    if (!newEnterpriseName.trim()) return;

    try {
      const enterprise = await createEnterpriseMutation.mutateAsync(newEnterpriseName.trim());
      onChange('professionalSituation', {
        ...proSituation,
        employerId: enterprise.id,
        employerName: enterprise.name,
        companyName: enterprise.name,
      });
      setIsCreatingEnterprise(false);
      setNewEnterpriseName('');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleCancelCreate = () => {
    setIsCreatingEnterprise(false);
    setNewEnterpriseName('');
  };

  const renderEnterpriseField = (label: string, required: boolean = false) => {
    if (isCreatingEnterprise) {
      return (
        <div className="flex items-end gap-1.5">
          <div className="min-w-0 flex-1">
            <Input
              label={label}
              value={newEnterpriseName}
              onChange={(e) => setNewEnterpriseName(e.target.value)}
              inputSize="sm"
              placeholder="Nom de l'entreprise"
              onKeyDown={(e) => e.key === 'Enter' && handleCreateEnterprise()}
              state={createEnterpriseMutation.isError ? 'error' : 'default'}
              helperText={createEnterpriseMutation.isError ? getErrorMessage('generic') : undefined}
            />
          </div>
          <Button
            preset="field-action-confirm"
            type="button"
            onClick={handleCreateEnterprise}
            disabled={!newEnterpriseName.trim()}
            loading={createEnterpriseMutation.isPending}
            tooltipText="Créer l'entreprise"
            className="shrink-0"
          />
          <Button
            preset="field-action-cancel"
            type="button"
            onClick={handleCancelCreate}
            className="shrink-0"
          />
        </div>
      );
    }

    return (
      <div className="flex items-end gap-1.5">
        <div className="min-w-0 flex-1">
          <Autocomplete
            label={label}
            placeholder="Rechercher une entreprise..."
            options={enrichedEnterpriseOptions}
            value={proSituation.employerId || ''}
            onChange={(val) => handleEnterpriseChange(val, enrichedEnterpriseOptions)}
            onSearch={handleEnterpriseSearch}
            isLoading={enterpriseLoading}
            required={required}
            minSearchLength={2}
            inputSize="sm"
          />
        </div>
        <Button
          preset="field-action-add"
          type="button"
          onClick={() => setIsCreatingEnterprise(true)}
          tooltipText="Vous ne trouvez pas l'entreprise ? Cliquez pour en ajouter une"
          className="shrink-0"
        />
      </div>
    );
  };

  const valuedEngagementRequired =
    persona === 'tutor' && isRemoteValuedEngagementRequired(currentStatus);

  /** Persist the associative-engagement answer. */
  function handleAssociativeEngagementChange(value: string): void {
    handleChange('associativeEngagement', value);
  }

  /** Valued-engagement select; required for every remote tutor situation. */
  function renderAssociativeEngagementSelect(): React.ReactNode {
    return (
      <Select
        label="Engagement associatif valorisé"
        labelTooltip={ASSOCIATIVE_ENGAGEMENT_TOOLTIP}
        value={proSituation.associativeEngagement || ''}
        onChange={handleAssociativeEngagementChange}
        options={YES_NO_OPTIONS}
        size="sm"
        placeholder="Sélectionner"
        required={valuedEngagementRequired}
      />
    );
  }

  const renderHighSchoolFields = () => (
    <>
      <Autocomplete
        label="Établissement scolaire"
        placeholder="Rechercher un lycée..."
        options={enrichedHighSchoolOptions}
        value={proSituation.establishment || ''}
        onChange={(val) => handleEstablishmentChange(val, enrichedHighSchoolOptions)}
        onSearch={handleHighSchoolEstablishmentSearch}
        isLoading={highSchoolEstablishmentLoading}
        required
        minSearchLength={2}
        inputSize="sm"
      />
      <Select
        label="Niveau d'études"
        value={proSituation.level || ''}
        onChange={(val) => handleChange('level', val)}
        options={[...STUDY_LEVEL_OPTIONS]}
        required
        size="sm"
        placeholder="Sélectionner un niveau"
      />
      <Select
        label="Cursus scolaire"
        value={proSituation.curriculum || ''}
        onChange={(val) => handleChange('curriculum', val)}
        options={HIGH_SCHOOL_CURRICULUM_OPTIONS}
        required
        size="sm"
        placeholder="Sélectionner"
      />
      {renderAssociativeEngagementSelect()}
    </>
  );

  const renderStudentFields = () => (
    <>
      <Autocomplete
        label="Établissement d'étude"
        placeholder="Rechercher un établissement..."
        options={enrichedHigherEdOptions}
        value={proSituation.establishment || ''}
        onChange={(val) => handleEstablishmentChange(val, enrichedHigherEdOptions)}
        onSearch={handleHigherEdEstablishmentSearch}
        isLoading={higherEdEstablishmentLoading}
        required
        minSearchLength={2}
        inputSize="sm"
      />
      <Select
        label="Niveau d'études"
        value={proSituation.level || ''}
        onChange={(val) => handleChange('level', val)}
        options={[...STUDY_LEVEL_OPTIONS]}
        required
        size="sm"
        placeholder="Sélectionner un niveau"
      />
      {renderAssociativeEngagementSelect()}
      <Select
        label="Cursus étudiant"
        value={proSituation.studentCurriculum || ''}
        onChange={(val) => handleChange('studentCurriculum', val)}
        options={TUTORING_COURSE_OPTIONS}
        size="sm"
        placeholder="Sélectionner une filière"
      />
      <Input
        label="Précisez son cursus étudiant"
        value={proSituation.curriculumDetail || ''}
        onChange={(e) => handleChange('curriculumDetail', e.target.value)}
        inputSize="sm"
        placeholder="Ex: Master Informatique"
      />
    </>
  );

  const renderEmployesEntrepreneursFields = () => {
    if (persona === 'tutor') {
      return (
        <>
          <Select
            label="Secteur d'activité"
            value={proSituation.fieldOfActivity || ''}
            onChange={(val) => handleChange('fieldOfActivity', val)}
            options={[...ACTIVITY_SECTOR_OPTIONS]}
            required
            size="sm"
            placeholder="Sélectionner un secteur"
          />
          {renderEnterpriseField('Entreprise', true)}
          <DepartmentSelect
            label="Département d'activité"
            value={proSituation.activityDepartment || ''}
            onChange={(val) => handleChange('activityDepartment', val)}
            required
            inputSize="sm"
          />
          <Select
            label={partnerLabel}
            value={proSituation.isCompanyPartner || ''}
            onChange={(val) => handleChange('isCompanyPartner', val)}
            options={YES_NO_OPTIONS}
            size="sm"
            placeholder="Sélectionner"
          />
          <Select
            label="Son entreprise valorise-t-elle ses engagements associatifs ?"
            value={proSituation.isEngagementValorised || ''}
            onChange={(val) => handleChange('isEngagementValorised', val)}
            options={YES_NO_OPTIONS}
            size="sm"
            placeholder="Sélectionner"
          />
          {renderAssociativeEngagementSelect()}
        </>
      );
    } else {
      return (
        <>
          <Select
            label="Secteur d'activité"
            value={proSituation.fieldOfActivity || ''}
            onChange={(val) => handleChange('fieldOfActivity', val)}
            options={[...ACTIVITY_SECTOR_OPTIONS]}
            size="sm"
            placeholder="Sélectionner un secteur"
          />
          <Input
            label="Précisez son secteur d'activité"
            value={proSituation.specifiedActivityField || ''}
            onChange={(e) => handleChange('specifiedActivityField', e.target.value)}
            inputSize="sm"
            placeholder="Détails sur le secteur"
          />
          {renderEnterpriseField('Entreprise', false)}
          <DepartmentSelect
            label="Département d'activité"
            value={proSituation.activityDepartment || ''}
            onChange={(val) => handleChange('activityDepartment', val)}
            inputSize="sm"
          />
        </>
      );
    }
  };

  const renderFonctionnairesFields = () => {
    if (persona === 'tutor') {
      return (
        <>
          <Select
            label="Secteur d'activité"
            value={proSituation.fieldOfActivity || ''}
            onChange={(val) => handleChange('fieldOfActivity', val)}
            options={[...ACTIVITY_SECTOR_OPTIONS]}
            required
            size="sm"
            placeholder="Sélectionner un secteur"
          />
          {renderEnterpriseField('Organisme / Administration', true)}
          <Select
            label="Grande fonction publique"
            value={proSituation.civilServantBranch || ''}
            onChange={(val) => handleChange('civilServantBranch', val)}
            options={CIVIL_SERVICE_BRANCH_OPTIONS}
            size="sm"
            placeholder="Sélectionner"
          />
          <DepartmentSelect
            label="Département d'activité"
            value={proSituation.activityDepartment || ''}
            onChange={(val) => handleChange('activityDepartment', val)}
            required
            inputSize="sm"
          />
          <Select
            label={partnerLabel}
            value={proSituation.isCompanyPartner || ''}
            onChange={(val) => handleChange('isCompanyPartner', val)}
            options={YES_NO_OPTIONS}
            size="sm"
            placeholder="Sélectionner"
          />
          <Select
            label="Son entreprise valorise-t-elle ses engagements associatifs ?"
            value={proSituation.isEngagementValorised || ''}
            onChange={(val) => handleChange('isEngagementValorised', val)}
            options={YES_NO_OPTIONS}
            size="sm"
            placeholder="Sélectionner"
          />
          {renderAssociativeEngagementSelect()}
        </>
      );
    } else {
      return null;
    }
  };

  const renderInactiveRetiredFields = () => (
    <>
      <Select
        label="Secteur d'activité"
        value={proSituation.fieldOfActivity || ''}
        onChange={(val) => handleChange('fieldOfActivity', val)}
        options={
          persona === 'tutor' ? [...FIELD_OF_ACTIVITY_TUTOR_OPTIONS] : [...ACTIVITY_SECTOR_OPTIONS]
        }
        size="sm"
        placeholder="Sélectionner un secteur (optionnel)"
      />
      {persona === 'tutor' ? renderAssociativeEngagementSelect() : null}
    </>
  );

  const renderConditionalFields = () => {
    if (currentStatus === 'high_school_student') {
      return renderHighSchoolFields();
    } else if (currentStatus === 'student') {
      return renderStudentFields();
    } else if (currentStatus === 'employee' || currentStatus === 'entrepreneur') {
      return renderEmployesEntrepreneursFields();
    } else if (currentStatus === 'civil_servant') {
      return renderFonctionnairesFields();
    } else if (currentStatus === 'unemployed' || currentStatus === 'retired') {
      return renderInactiveRetiredFields();
    } else if (persona === 'tutor' && currentStatus) {
      return renderAssociativeEngagementSelect();
    }
    return null;
  };

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <Briefcase className="w-4 h-4" />
        Situation professionnelle
      </h4>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Select
            label="Situation professionnelle"
            value={currentStatus}
            onChange={handleStatusChange}
            options={PRO_SITUATION_STATUS_OPTIONS}
            required
            size="sm"
            placeholder="Sélectionner sa situation"
          />
        </div>
        {renderConditionalFields()}
      </div>
    </div>
  );
};
